import React from 'react';
import { Brain, Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export default function PrivacyPage() {
  useDocumentTitle('Privacy Policy');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Navigation */}
      <nav className="border-b border-border-light bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-text-primary">Campus Connect</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-primary-500 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-10 h-10 text-primary-500" />
          <h1 className="text-3xl font-bold text-text-primary">Privacy Policy</h1>
        </div>

        <div className="bg-surface-light rounded-xl shadow-md p-8 space-y-8">
          <p className="text-sm text-text-muted">Last updated: March 3, 2026</p>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Overview</h2>
            <p className="text-text-secondary leading-relaxed">
              CampusConnect ("we," "our," or "us") is committed to protecting the privacy of educators,
              administrators, and staff who use our platform. This Privacy Policy explains how we collect,
              use, and safeguard your information when you use CampusConnect at campusconnectedu.com.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-text-primary mb-1">Account Information</h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  When you create an account, we collect your name, email address, school or institution name,
                  and role (administrator, teacher, staff). This information is stored securely in Firebase
                  Authentication and Firestore.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-1">School Data</h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Information you enter about your school, including team members, calendar events, tasks,
                  documents, messages, and attendance records. This data belongs to your school and is only
                  accessible to authorized members of your school.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-1">Usage Data</h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  We collect anonymized usage analytics to improve the platform, including pages visited,
                  features used, and general interaction patterns. We do not track individual user behavior
                  for advertising purposes.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-text-secondary text-sm">
              <li>To provide and maintain the CampusConnect platform</li>
              <li>To authenticate users and manage access to school data</li>
              <li>To send notifications about events, tasks, and messages within your school</li>
              <li>To process subscription payments through Stripe</li>
              <li>To improve our platform based on usage patterns</li>
              <li>To provide customer support when you contact us</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Data Security</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              We use industry-standard security measures to protect your data. All data is encrypted in
              transit using TLS/SSL. User authentication is handled by Firebase Authentication with
              secure password hashing. Firestore security rules ensure users can only access data
              belonging to their school. Payment processing is handled entirely by Stripe — we never
              store credit card numbers on our servers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Student Data (FERPA Compliance)</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              CampusConnect is designed for school staff communication and administration. We do not
              directly collect student personally identifiable information (PII). If your school chooses
              to include student-related information in documents, tasks, or messages, that data is
              protected under the same security measures as all other school data and is only accessible
              to authorized members of your school. We support schools in maintaining FERPA compliance
              by providing role-based access controls and data isolation between schools.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Third-Party Services</h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-2">
              We use the following third-party services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary text-sm">
              <li><strong>Firebase</strong> (Google) — Authentication, database, and file storage</li>
              <li><strong>Stripe</strong> — Payment processing for subscriptions</li>
              <li><strong>Google Calendar API</strong> — Calendar sync (only when you connect your calendar)</li>
              <li><strong>Canvas / Infinite Campus</strong> — LMS integration (only when you connect your LMS)</li>
            </ul>
            <p className="text-text-secondary text-sm leading-relaxed mt-2">
              Each of these services has its own privacy policy. We only share the minimum data
              necessary for each service to function.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Data Retention & Deletion</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Your data is retained as long as your account is active. You can delete your account at any
              time from your Profile settings, which will remove your personal data. School administrators
              can delete their school and all associated data. After account deletion, we may retain
              anonymized, aggregated data for analytics purposes, but all personally identifiable
              information is permanently removed within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Your Rights</h2>
            <ul className="list-disc list-inside space-y-2 text-text-secondary text-sm">
              <li>Access your personal data at any time through your Profile page</li>
              <li>Update or correct your information through Settings</li>
              <li>Delete your account and associated data</li>
              <li>Export your school data (contact support)</li>
              <li>Opt out of non-essential communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">Contact Us</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              If you have questions about this Privacy Policy or how we handle your data, contact us at{' '}
              <a href="mailto:privacy@sketchmyapp.com" className="text-primary-500 hover:underline">
                privacy@sketchmyapp.com
              </a>.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-text-muted">
          <div className="flex justify-center gap-6">
            <Link to="/terms" className="hover:text-primary-500 transition-colors">Terms of Service</Link>
            <Link to="/support" className="hover:text-primary-500 transition-colors">Support</Link>
            <Link to="/getting-started" className="hover:text-primary-500 transition-colors">Getting Started</Link>
          </div>
        </div>
      </div>
    </div>
  );
}